import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";

const buttonVariants = cva(
  "inline-flex items-center disabled:opacity-50 cursor-pointer justify-center gap-2.5  rounded-md font-medium transition-all disabled:pointer-events-none [&_svg]:pointer-events-none disabled:cursor-not-allowed focus-visible:ring-offset-2 [&_svg:not([class*='size-'])]:size-5 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-[3px]",
  {
    variants: {
      variant: {
        primary:
          "bg-pink-primary focus-visible:ring-pink-primary/50  text-white-primary hover:pink-overlay duration-300 ease-out",
        secondary:
          "bg-white-primary border border-light-primary text-dark-primary hover:bg-dark-primary hover:text-white-primary duration-300 ease-out focus-visible:ring-light-primary/90 ",
        light:
          "bg-secondary-light focus-visible:ring-pink-light/20 text-dark-primary hover:light-overlay duration-300 ease-out",
        rounded:
          "bg-dark-primary focus-visible:ring-dark-primary text-white-primary hover:bg-dark-primary/70 duration-300 ease-out",
        slider:
          "bg-dark-primary/60 focus-visible:ring-dark-primary/50 text-white-primary hover:bg-dark-primary/95 duration-300 ease-out",
        danger:
          "bg-red-500 text-white-primary hover:bg-red-600 duration-300 ease-in-out focus-visible:ring-red-600/50 ",
      },
      size: {
        lg: "py-4.5 px-5 text-lg",
        md: "px-4 py-3",
        sm: "py-1.5 px-2.5 text-sm",
        icon: "p-2.5 rounded-full",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "lg",
    },
  }
);

export type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { Button, buttonVariants };
